import { forwardRef } from 'react'
import '../../css/Vitae.css'

const FONT_PAIRS = [
   { body: 'Georgia, serif', heading: "'Playfair Display', serif" },
   { body: "'Inter', sans-serif", heading: "'Inter', sans-serif" },
   { body: "'Lato', sans-serif", heading: "'Cormorant Garamond', serif" },
   {
      body: "'IBM Plex Sans', sans-serif",
      heading: "'IBM Plex Mono', monospace",
   },
   { body: "'EB Garamond', serif", heading: "'Cinzel', serif" },
]

/* ── Shared atoms ── */

function SecTitle({ title, accent }) {
   return (
      <div
         className="vt-section-title"
         style={{ borderColor: accent, color: accent }}>
         {title}
      </div>
   )
}

function BulletList({ items, light }) {
   const rows = items?.filter(i => i.value) ?? []
   if (!rows.length) return null
   return (
      <ul className={`vt-list${light ? ' vt-list-light' : ''}`}>
         {rows.map(i => (
            <li key={i.id}>{i.value}</li>
         ))}
      </ul>
   )
}

function Pills({ items, accent }) {
   const rows = items?.filter(i => i.value) ?? []
   if (!rows.length) return null
   return (
      <div className="vt-pills">
         {rows.map(s => (
            <span
               key={s.id}
               className="vt-pill"
               style={{ borderColor: accent, color: accent }}>
               {s.value}
            </span>
         ))}
      </div>
   )
}

const EDU_LEVELS = [
   { key: 'elementary', label: 'Elementary' },
   { key: 'highschool', label: 'High School' },
   { key: 'seniorhigh', label: 'Senior High' },
   { key: 'college', label: 'College' },
]

function EduBlock({ edu, accent, light }) {
   if (!edu || !EDU_LEVELS.some(l => edu[l.key]?.school)) return null
   const textColor = light ? 'rgba(255,255,255,0.9)' : '#1a1a1a'
   const yearColor = light ? 'rgba(255,255,255,0.55)' : '#666'
   const achvColor = light ? 'rgba(255,255,255,0.75)' : '#444'
   return (
      <div className="vt-edu-grid">
         {EDU_LEVELS.map(({ key, label }) => {
            const d = edu[key]
            if (!d?.school) return null
            const achievements = d.achievements?.filter(a => a.value) ?? []
            return (
               <div key={key} className="vt-edu-item">
                  <div className="vt-edu-level" style={{ color: accent }}>
                     {label}
                  </div>
                  <div className="vt-edu-school" style={{ color: textColor }}>
                     {d.school}
                  </div>
                  {(d.yearStart || d.yearEnd) && (
                     <div className="vt-edu-year" style={{ color: yearColor }}>
                        {d.yearStart} – {d.yearEnd}
                     </div>
                  )}
                  {achievements.length > 0 && (
                     <ul className="vt-edu-achievements">
                        {achievements.map(a => (
                           <li key={a.id} style={{ color: achvColor }}>
                              {a.value}
                           </li>
                        ))}
                     </ul>
                  )}
               </div>
            )
         })}
      </div>
   )
}
/* Education displayed as single column, college first → elementary last */
const EDU_LEVELS_DESC = [...EDU_LEVELS].reverse()

function EduColumn({ edu, accent, light }) {
   if (!edu || !EDU_LEVELS.some(l => edu[l.key]?.school)) return null
   const textColor = light ? 'rgba(255,255,255,0.9)' : '#1a1a1a'
   const yearColor = light ? 'rgba(255,255,255,0.55)' : '#666'
   const achvColor = light ? 'rgba(255,255,255,0.75)' : '#444'
   return (
      <div className="vt-edu-col">
         {EDU_LEVELS_DESC.map(({ key, label }) => {
            const d = edu[key]
            if (!d?.school) return null
            const achievements = d.achievements?.filter(a => a.value) ?? []
            return (
               <div key={key} className="vt-edu-item">
                  <div className="vt-edu-level" style={{ color: accent }}>
                     {label}
                  </div>
                  <div className="vt-edu-school" style={{ color: textColor }}>
                     {d.school}
                  </div>
                  {(d.yearStart || d.yearEnd) && (
                     <div className="vt-edu-year" style={{ color: yearColor }}>
                        {d.yearStart} – {d.yearEnd}
                     </div>
                  )}
                  {achievements.length > 0 && (
                     <ul className="vt-edu-achievements">
                        {achievements.map(a => (
                           <li key={a.id} style={{ color: achvColor }}>
                              {a.value}
                           </li>
                        ))}
                     </ul>
                  )}
               </div>
            )
         })}
      </div>
   )
}

function Block({ title, accent, children }) {
   return (
      <div className="vt-block">
         <SecTitle title={title} accent={accent} />
         {children}
      </div>
   )
}

function hasItems(arr) {
   return arr?.some(e => e.value)
}
function hasEdu(edu) {
   return edu && Object.values(edu).some(l => l.school)
}

function CVPhoto({ src, accent, cls = 'vt-photo' }) {
   return (
      <img src={src} className={cls} style={{ borderColor: accent }} alt="" />
   )
}

function Description({ text, fs, light = true }) {
   if (!text) return null
   return (
      <p
         className="vt-description"
         style={{
            fontSize: fs * 0.8 + 'px',
            color: light ? 'rgba(255,255,255,0.7)' : '#555',
         }}>
         {text}
      </p>
   )
}

/* Main body sections — experience, projects, references */
function MainSections({ field, ac, fs }) {
   return (
      <>
         {hasItems(field.experience) && (
            <Block title="Experience" accent={ac}>
               <BulletList items={field.experience} />
            </Block>
         )}
         {hasItems(field.projects) && (
            <Block title="Projects" accent={ac}>
               <BulletList items={field.projects} />
            </Block>
         )}
         {hasItems(field.reference) && (
            <Block title="References" accent={ac}>
               <BulletList items={field.reference} />
            </Block>
         )}
      </>
   )
}

/* ═══════════════════════════════════════
   Layout 0 — Full-width header above
   ═══════════════════════════════════════ */
function Layout0({ field, design, fp, fs, fh, sp }) {
   const { accentColor: ac, headerColor: hc, showPhoto } = design
   return (
      <div className="vt-l0">
         <div
            className="vt-l0-header"
            style={{
               background: hc,
               padding: `${sp * 1.5}rem ${sp * 2}rem`,
            }}>
            {showPhoto !== false && field.photo && (
               <CVPhoto src={field.photo} accent={ac} />
            )}
            <div>
               <h1
                  className="vt-name"
                  style={{
                     fontFamily: fp.heading,
                     fontSize: fh * 1.9 + 'px',
                  }}>
                  {field.fullname || 'Your Name'}
               </h1>
               {field.jobtitle && (
                  <p
                     className="vt-jobtitle"
                     style={{ color: ac, fontSize: fh * 0.9 + 'px' }}>
                     {field.jobtitle}
                  </p>
               )}
               <div className="vt-meta" style={{ fontSize: fh * 0.78 + 'px' }}>
                  {field.contact && <span>{field.contact}</span>}
                  {field.email && <span>{field.email}</span>}
               </div>
               <Description text={field.description} fs={fh} />
            </div>
         </div>
         <div
            className="vt-l0-body"
            style={{
               padding: `${sp * 1.4}rem ${sp * 2}rem`,
               fontSize: fs + 'px',
            }}>
            <MainSections field={field} ac={ac} />
            {hasItems(field.skills) && (
               <Block title="Skills" accent={ac}>
                  <Pills items={field.skills} accent={ac} />
               </Block>
            )}
            {hasEdu(field.education) && (
               <Block title="Education" accent={ac}>
                  <EduBlock edu={field.education} accent={ac} />
               </Block>
            )}
         </div>
      </div>
   )
}

/* ═══════════════════════════════════════
   Layout 1 — Left sidebar, full height
   sidebar: photo, name, jobtitle, contact, email, edu (column)
   main: description, skills, experience, projects, references
   ═══════════════════════════════════════ */
function Layout1({ field, design, fp, fs, fh, sp }) {
   const { accentColor: ac, headerColor: hc, showPhoto } = design
   return (
      <div className="vt-l1" style={{ fontSize: fs + 'px' }}>
         <div
            className="vt-l1-sidebar"
            style={{
               background: hc,
               padding: `${sp * 2}rem ${sp * 1.2}rem`,
            }}>
            {showPhoto !== false && field.photo && (
               <CVPhoto
                  src={field.photo}
                  accent={ac}
                  cls="vt-photo vt-photo-sm"
               />
            )}
            <h1
               className="vt-name-sm"
               style={{ fontFamily: fp.heading, fontSize: fh * 1.2 + 'px' }}>
               {field.fullname || 'Your Name'}
            </h1>
            {field.jobtitle && (
               <p
                  className="vt-jobtitle vt-jobtitle-sm"
                  style={{ color: ac, fontSize: fh * 0.8 + 'px' }}>
                  {field.jobtitle}
               </p>
            )}
            {field.contact && (
               <p className="vt-meta-sm" style={{ fontSize: fh * 0.75 + 'px' }}>
                  {field.contact}
               </p>
            )}
            {field.email && (
               <p className="vt-meta-sm" style={{ fontSize: fh * 0.75 + 'px' }}>
                  {field.email}
               </p>
            )}
            {hasEdu(field.education) && (
               <div
                  className="vt-block"
                  style={{ marginTop: `${sp}rem`, width: '100%' }}>
                  <div
                     className="vt-section-title vt-section-sm"
                     style={{ borderColor: ac, color: ac }}>
                     Education
                  </div>
                  <EduColumn edu={field.education} accent={ac} light />
               </div>
            )}
         </div>
         <div
            className="vt-l1-main"
            style={{ padding: `${sp * 2}rem ${sp * 1.5}rem` }}>
            <Description text={field.description} fs={fs} light={false} />
            {hasItems(field.skills) && (
               <Block title="Skills" accent={ac}>
                  <Pills items={field.skills} accent={ac} />
               </Block>
            )}
            <MainSections field={field} ac={ac} />
         </div>
      </div>
   )
}

/* ═══════════════════════════════════════
   Layout 2 — Sidebar (photo/contact/edu) + rich main
   sidebar: photo, name, jobtitle, contact, email, edu (column)
   main: description, skills, experience, projects, references
   ═══════════════════════════════════════ */
function Layout2({ field, design, fp, fs, fh, sp }) {
   const { accentColor: ac, headerColor: hc, showPhoto } = design
   return (
      <div className="vt-l2" style={{ fontSize: fs + 'px' }}>
         <div
            className="vt-l2-sidebar"
            style={{
               background: hc,
               padding: `${sp * 2}rem ${sp * 1.2}rem`,
            }}>
            {showPhoto !== false && field.photo && (
               <CVPhoto
                  src={field.photo}
                  accent={ac}
                  cls="vt-photo vt-photo-center-sm"
               />
            )}
            <h1
               className="vt-name-center"
               style={{
                  fontFamily: fp.heading,
                  fontSize: fh * 1.1 + 'px',
                  color: '#fff',
               }}>
               {field.fullname || 'Your Name'}
            </h1>
            {field.jobtitle && (
               <p
                  className="vt-jobtitle vt-jobtitle-center"
                  style={{ color: ac, fontSize: fh * 0.8 + 'px' }}>
                  {field.jobtitle}
               </p>
            )}
            {field.contact && (
               <p
                  className="vt-meta-sm vt-meta-sm-center"
                  style={{ fontSize: fh * 0.75 + 'px' }}>
                  {field.contact}
               </p>
            )}
            {field.email && (
               <p
                  className="vt-meta-sm vt-meta-sm-center"
                  style={{ fontSize: fh * 0.75 + 'px' }}>
                  {field.email}
               </p>
            )}
            {hasEdu(field.education) && (
               <div
                  className="vt-block"
                  style={{ marginTop: `${sp}rem`, width: '100%' }}>
                  <div
                     className="vt-section-title vt-section-sm"
                     style={{ borderColor: ac, color: ac }}>
                     Education
                  </div>
                  <EduColumn edu={field.education} accent={ac} light />
               </div>
            )}
         </div>
         <div
            className="vt-l2-main"
            style={{ padding: `${sp * 1.5}rem ${sp * 1.5}rem` }}>
            <Description text={field.description} fs={fs} light={false} />
            {hasItems(field.skills) && (
               <Block title="Skills" accent={ac}>
                  <Pills items={field.skills} accent={ac} />
               </Block>
            )}
            <MainSections field={field} ac={ac} />
         </div>
      </div>
   )
}

/* ═══════════════════════════════════════
   Layout 3 — Centered header + two-col body
   header: photo, name, jobtitle, contact, email
   body col-left: description, experience, projects
   body col-right: skills, references, education
   ═══════════════════════════════════════ */
function Layout3({ field, design, fp, fs, fh, sp }) {
   const { accentColor: ac, headerColor: hc, showPhoto } = design
   return (
      <div className="vt-l3">
         <div
            className="vt-l3-header"
            style={{
               background: hc,
               padding: `${sp * 1.5}rem ${sp * 2}rem`,
            }}>
            {showPhoto !== false && field.photo && (
               <CVPhoto
                  src={field.photo}
                  accent={ac}
                  cls="vt-photo vt-photo-center"
               />
            )}
            <h1
               className="vt-name-center"
               style={{
                  fontFamily: fp.heading,
                  fontSize: fh * 1.9 + 'px',
                  color: '#fff',
               }}>
               {field.fullname || 'Your Name'}
            </h1>
            {field.jobtitle && (
               <p
                  className="vt-jobtitle vt-jobtitle-center"
                  style={{ color: ac, fontSize: fh * 0.9 + 'px' }}>
                  {field.jobtitle}
               </p>
            )}
            <div
               className="vt-meta vt-meta-center"
               style={{ fontSize: fh * 0.78 + 'px' }}>
               {field.contact && <span>{field.contact}</span>}
               {field.email && <span>{field.email}</span>}
            </div>
            <div className="vt-l3-divider" style={{ background: ac }} />
         </div>
         <div
            className="vt-l3-body"
            style={{
               padding: `${sp * 1.2}rem ${sp * 2}rem`,
               fontSize: fs + 'px',
            }}>
            <div className="vt-l3-col">
               <Description text={field.description} fs={fs} light={false} />
               {hasItems(field.experience) && (
                  <Block title="Experience" accent={ac}>
                     <BulletList items={field.experience} />
                  </Block>
               )}
               {hasItems(field.projects) && (
                  <Block title="Projects" accent={ac}>
                     <BulletList items={field.projects} />
                  </Block>
               )}
            </div>
            <div className="vt-l3-col">
               {hasItems(field.skills) && (
                  <Block title="Skills" accent={ac}>
                     <Pills items={field.skills} accent={ac} />
                  </Block>
               )}
               {hasItems(field.reference) && (
                  <Block title="References" accent={ac}>
                     <BulletList items={field.reference} />
                  </Block>
               )}
               {hasEdu(field.education) && (
                  <Block title="Education" accent={ac}>
                     <EduBlock edu={field.education} accent={ac} />
                  </Block>
               )}
            </div>
         </div>
      </div>
   )
}

/* ── Main export ── */
const LAYOUTS = [Layout0, Layout1, Layout2, Layout3]

const Vitae = forwardRef(function Vitae({ field, design, style }, ref) {
   const fp = FONT_PAIRS[design?.fontPair ?? 0]
   const fs = design?.fontSize ?? 13 // body font size
   const fh = design?.fontSizeHeader ?? 13 // header font size
   const sp = design?.spacing ?? 1
   const Layout = LAYOUTS[design?.layout ?? 0]
   return (
      <div ref={ref} className="CV" style={{ ...style, fontFamily: fp.body }}>
         <Layout
            field={field}
            design={design}
            fp={fp}
            fs={fs}
            fh={fh}
            sp={sp}
         />
      </div>
   )
})

export default Vitae
